import React, { useEffect, useMemo, useState } from 'react';

// scripts/archive/build.mjs 가 만든 data/archive/index.json 의 문항. Go 서버가 /archive/ 로 내보낸다
export type ArchiveItem = {
  id: string;
  year: number;
  exam: string;
  track: string;
  subject: string;
  number: number;
  page: number;
  image: string;
  answer: string | null;
  points: number | null;
};

type Choice = { key: string; label: string; items: ArchiveItem[] };

/** 한 학년도의 고를 거리. 선택과목이 있으면 공통 문항에 그 과목 문항을 붙여 1~30 을 만든다 */
function choicesOf(items: ArchiveItem[]): Choice[] {
  const tracks = [...new Set(items.map((i) => i.track))];
  return tracks.flatMap((track) => {
    const own = items.filter((i) => i.track === track);
    const common = own.filter((i) => i.subject === track || i.subject === '공통');
    const electives = [...new Set(own.map((i) => i.subject).filter((s) => s !== track && s !== '공통'))];
    if (electives.length === 0) return [{ key: track, label: track, items: common }];
    return electives.map((s) => ({
      key: `${track} ${s}`,
      label: track === '통합' ? s : `${track} ${s}`,
      items: [...common, ...own.filter((i) => i.subject === s)].sort((a, b) => a.number - b.number),
    }));
  });
}

export const imageUrl = (item: ArchiveItem) => `./archive/${item.image.split('/').map(encodeURIComponent).join('/')}`;

export const itemTitle = (item: ArchiveItem) =>
  `${item.year}학년도 ${item.exam} ${item.subject === item.track || item.subject === '공통' ? item.track === '통합' ? '공통' : item.track : item.subject} ${item.number}번`;

export function useArchive() {
  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    fetch('./archive/index.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`index.json ${r.status}`))))
      .then((data: { items: ArchiveItem[] }) => setItems(data.items))
      .catch((e: Error) => setError(e.message));
  }, []);
  return { items, error };
}

/** 연도 탭, 유형과 과목, 1~30 격자. 고른 문항을 onPick 으로 알린다 */
export function ArchivePicker({ items, onPick }: { items: ArchiveItem[]; onPick: (item: ArchiveItem | undefined) => void }) {
  const [year, setYear] = useState(0);
  const [choiceKey, setChoiceKey] = useState('');
  const [number, setNumber] = useState(1);
  const years = useMemo(() => [...new Set(items.map((i) => i.year))].sort((a, b) => b - a), [items]);
  const current = year || years[0] || 0;
  const choices = useMemo(() => choicesOf(items.filter((i) => i.year === current)), [items, current]);
  const choice = choices.find((c) => c.key === choiceKey) ?? choices[0];
  const item = choice?.items.find((i) => i.number === number);
  useEffect(() => onPick(item), [item, onPick]);
  return (
    <>
      <span className="tabs">{years.map((y) => <button key={y} type="button" className={y === current ? 'is-on' : undefined} onClick={() => setYear(y)}>{y}학년도</button>)}</span>
      <div className="archive__row">
        <span className="seg">{choices.map((c) => <button key={c.key} type="button" className={c.key === choice?.key ? 'is-on' : undefined} onClick={() => setChoiceKey(c.key)}>{c.label}</button>)}</span>
      </div>
      <div className="archive__grid tabs">
        {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => {
          const has = choice?.items.some((it) => it.number === n);
          return <button key={n} type="button" className={n === number ? 'is-on' : undefined} disabled={!has} onClick={() => setNumber(n)}>{n}</button>;
        })}
      </div>
    </>
  );
}

export function ItemChips({ item }: { item: ArchiveItem }) {
  return (
    <span className="chips">
      {item.points !== null ? <span className="chip">{item.points}점</span> : null}
      {item.answer !== null ? <span className="chip chip--accent">정답 {item.answer}</span> : <span className="chip chip--warn">정답 없음</span>}
    </span>
  );
}

export function ArchiveView() {
  const { items, error } = useArchive();
  const [item, setItem] = useState<ArchiveItem>();
  return (
    <div className="main">
      <section className="work">
        <div className="panel archive__filters">
          <div className="panel__head"><span>기출 탐색</span><span className="muted">대학수학능력시험 수학 홀수형 · {items.length}문항{error ? ` · 불러오지 못함: ${error}` : ''}</span></div>
          <div className="panel__body"><ArchivePicker items={items} onPick={setItem} /></div>
        </div>
        <div className="panel archive__item">
          <div className="panel__head"><span>{item ? itemTitle(item) : '문항'}</span>{item ? <ItemChips item={item} /> : null}</div>
          <div className="panel__body">{item ? <img className="archive__image" src={imageUrl(item)} alt={item.id} /> : <span className="muted">이 번호의 문항이 없습니다.</span>}</div>
        </div>
      </section>
    </div>
  );
}
